import { useEffect, useState } from "react";
import { useNavigate, Outlet } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { BrandSidebar } from "./BrandSidebar";
import { Loader2, Home, User, Settings as SettingsIcon, LogOut, Gift, ShieldCheck } from "lucide-react";
import { useGreeting } from "@/hooks/useGreeting";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function BrandLayout() {
  const [loading, setLoading] = useState(true);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [brandName, setBrandName] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const navigate = useNavigate();
  const greeting = useGreeting();

  useEffect(() => {
    checkBrandAccess();
  }, []);

  const checkBrandAccess = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      setUser(currentUser);

      // Check if user has brand role
      const { data: hasRole } = await supabase.rpc('has_role', {
        _user_id: currentUser.id,
        _role: 'brand'
      });

      if (!hasRole) {
        navigate('/');
        return;
      }

      // Fetch brand details
      const { data: brand } = await supabase
        .from('verified_brands')
        .select('id, brand_name')
        .eq('user_id', currentUser.id)
        .single();

      if (brand) {
        setBrandId(brand.id);
        setBrandName(brand.brand_name);
      }

      // Fetch first name
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, full_name')
        .eq('id', currentUser.id)
        .single();
      const name = profile?.display_name || profile?.full_name;
      setFirstName(name ? name.split(' ')[0] : currentUser.email?.split('@')[0] || null);
    } catch (error) {
      console.error('Error checking brand access:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!brandId) {
    return null;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex flex-col w-full">
        {/* Header */}
        <header className="h-16 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
          <div className="h-full px-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SidebarTrigger />
              <h1 className="text-lg font-semibold">Brand Portal</h1>
            </div>
            
            {user && (
              <div className="flex items-center gap-3">
                {firstName && (
                  <span className="hidden md:inline text-sm font-medium text-foreground/80">
                    {greeting}, {firstName}
                  </span>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {user.email?.charAt(0).toUpperCase() || 'B'}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/')}>
                    <Home className="mr-2 h-4 w-4" />
                    Home
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/referrals')}>
                    <Gift className="mr-2 h-4 w-4" />
                    Referrals
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/reputation')}>
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Reputation & Disbursement
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <SettingsIcon className="mr-2 h-4 w-4" />
                    Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <div className="flex flex-1 w-full">
          <BrandSidebar brandName={brandName} />
          <main className="flex-1 p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
